import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, take, tap } from 'rxjs/operators';

import { Observable, of } from 'rxjs';
import { Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userData: any;
  storedData: any;
  apiUrl = environment.apiUrl;
  readonly testUrl = 'https://test.api.tridimo.de/api/';
  readonly ProductionUrl = 'https://api.tridimo.de/api/';
  readonly devUrl = 'https://dev.api.tridimo.de/api/';
  sessionCheckInterval = 2 * 60 * 60 * 1000;
  sessionCheckTimeout: any;
  private sessionCheckInProgress = false;
  private isLoggingOut = false;


    /**
   * Konstruktor des AuthService.
   * Initialisiert HttpClient, Router und prüft beim erneuten Aktivieren des Tabs,
   * ob die Benutzersitzung noch gültig ist.
   */
constructor(
  private http: HttpClient,
  private router: Router,
  @Inject(PLATFORM_ID) private platformId: Object
) {
  if (isPlatformBrowser(this.platformId)) {
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden && !this.sessionCheckInProgress) {
        // console.log("Tab wurde wieder aktiviert, Sitzung wird überprüft...");
        this.sessionCheckInProgress = true;
        this.checkSessionTimeout();
      }
    });
  }
}

/**
   * Erstellt die Authentifizierungsoptionen für API-Requests.
   * Nutzt entweder Token-basierte Authentifizierung oder Cookies,
   * abhängig von der Konfiguration in der Environment-Datei.
   */
 private getAuthOptions(): { headers?: HttpHeaders; withCredentials?: boolean } {
    const useTokenAuth = environment.useTokenAuth;
    const storedData = localStorage.getItem('authUser');
    let token = '';
    let exp = '';

    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        token = parsedData.token;
        exp = parsedData.exp;
        this.checkToken(exp);
      } catch (e) {
        this.logout();
      }
    } else {
      this.logout();
    }

    if (useTokenAuth) {
      return {
        headers: new HttpHeaders({
          'Authorization': `${token}`,
          'Content-Type': 'application/json'
        })
      };
    } else {
      return {
        withCredentials: true
      };
    }
  }

   /**
   * Lädt gespeicherte Benutzerdaten aus dem LocalStorage.
   * Die Daten werden nur im Browser-Kontext ausgelesen,
   * um Fehler bei Server-Side Rendering zu vermeiden.
   */
 getStoredData() {
  if (isPlatformBrowser(this.platformId)) {
    this.storedData = localStorage.getItem('authUser');
  }
}


  /**
   * Prüft, ob ein Token abgelaufen ist.
   * Ist das Ablaufdatum überschritten oder ungültig,
   * wird der Benutzer automatisch ausgeloggt.
   */
  checkToken(token: any) {
    try {
      const expirationTime = token * 1000;
      const currentTime = Date.now();
      if (currentTime - expirationTime > 3600000) {
        this.logout();
      }
    } catch (e) {
      this.logout();
    }
  }

    /**
   * Führt den Login des Benutzers durch.
   * Die erhaltenen Benutzerdaten werden nach erfolgreicher Anmeldung
   * im LocalStorage gespeichert.
   */
  login(data: any) {
    const request = this.http.post(`${this.apiUrl}auth/login`, data, {
      withCredentials: this.apiUrl === this.testUrl || this.apiUrl === this.ProductionUrl,
    });
    return request.pipe(
      tap((result) => {
        localStorage.setItem('authUser', JSON.stringify(result));
      })
    );
  }

    /**
   * Überprüft, ob die aktuelle Sitzung noch gültig ist.
   * Bei ungültiger oder abgelaufener Sitzung wird automatisch
   * ein Logout ausgelöst.
   */
  checkSessionTimeout() {
    if (!this.isLoggedIn()) {
      this.sessionCheckInProgress = false;
      return;
    }
  
    this.checkSession().subscribe({
      next: (response) => {
        // console.log('Sitzung', response);
        if (response.status === 401 || response.statusText === "Unauthorized") {
          this.logout();
        }
        this.sessionCheckInProgress = false;
      },
      error: (err) => {
        // console.log('Sitzung abgelaufen, Benutzer ausgeloggt', err);
        this.logout();
        this.sessionCheckInProgress = false;
      },
    });
  }

  /**
   * Sendet eine Anfrage an die API zur Validierung der Sitzung.
   * Fehler werden abgefangen und als Observable zurückgegeben,
   * damit die Anwendung stabil weiterlaufen kann.
   */
  public checkSession(): Observable<any> {
    return this.http.get(`${this.apiUrl}user/details`, this.getAuthOptions()).pipe(
      catchError((error) => of(error))
    );
  }

  /**
   * Führt den Logout-Prozess aus.
   * Der Benutzer wird serverseitig abgemeldet und anschließend
   * lokal aus der Anwendung ausgeloggt.
   */
logout() {
  if (this.isLoggingOut || !isPlatformBrowser(this.platformId)) return;
  this.isLoggingOut = true;

  const token = localStorage.getItem('authToken');

  this.http.post(`${this.apiUrl}auth/logout`, {}, this.getAuthOptions()).pipe(take(1)).subscribe({
    next: () => this.localLogout(),
    error: () => this.localLogout(),
  });

  if (!document.cookie || !token) {
    this.localLogout();
  }
}

/**
   * Löscht lokale Session- und Benutzerdaten.
   * Danach wird der Benutzer abhängig von der Umgebung
   * zur passenden Login-Seite weitergeleitet.
   */
localLogout() {
  sessionStorage.clear();
  localStorage.clear();
  if (this.apiUrl === this.ProductionUrl) {
    window.location.replace('https://auth.tridimo.de/login');
  } else if (this.apiUrl === this.testUrl) {
    window.location.replace('https://test.auth.tridimo.de/login');
  } 
  // else {
  //   window.location.replace('https://dev.auth.tridimo.de/login');
  // } 
}

 /**
   * Prüft, ob ein Benutzer aktuell eingeloggt ist.
   * Dazu wird kontrolliert, ob Benutzerdaten
   * im LocalStorage vorhanden sind.
   */
  isLoggedIn() {
    return localStorage.getItem('authUser') !== null;
  }

  /**
   * Prüft beim Sichtbarkeitswechsel des Tabs die Benutzersitzung.
   * Bei Authentifizierungsfehlern wird automatisch ausgeloggt,
   * andere Fehler werden lediglich protokolliert.
   */
  visibilitySessionCheck(): Observable<any> {
  return this.http.get(`${this.apiUrl}user`, this.getAuthOptions()).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 403) {
        // Nur bei echten Auth-Fehlern ausloggen
        this.logout();
      } else {
        // Bei anderen Fehlern ggf. nur loggen
        console.error('Fehler beim Laden der Userdaten:', error);
      }
      return of(null);
    })
  );
}

}
